import { Hidden, Link } from '@mui/material'
import cn from 'classnames'
import moment from 'moment'
import { GetStaticProps, NextPage } from 'next'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { CollectionContentPagination } from 'src/components/CollectionContentPagination/CollectionContentPagination'
import { CollectionVendorPagination } from 'src/components/CollectionVendorPagination/CollectionVendorPagination'
import { ContentWriter } from 'src/components/ContentWriter/ContentWriter'
import { Footer } from 'src/components/Footer/Footer'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { PremiumModal } from 'src/components/global/PremiumModal'
import { SearchField } from 'src/components/global/SearchField'
import { Sidebar } from 'src/components/global/Sidebar'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { TopicCategoriesTab } from 'src/components/Tabs/TopicCategoriesTab'
import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { useDecodedKeyword } from 'src/hooks/useGetDecodeKeyword'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption, TaxonomyType } from 'src/types'
import { getContentFilters, getDirectoryFilters } from 'src/utils/getFilters'
import { removeTags } from 'src/utils/removeTags'
import { withRetries } from 'src/utils/retryHelper'

import styles from './collections.module.scss'
type Props = {
  slug: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  collection: any
  filters: FilterOption[]
  contentFilters: FilterOption[]
}

const Collections: NextPage<Props> = ({ collection, filters, contentFilters }: Props) => {
  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()

  const [showModal, setShowModal] = useState(false)
  const { isPremium, isVendor, status } = useAuthenticated()
  // const filtersDisabled = useMemo(() => !isPremium, [isPremium])
  const title = collection?.name || ''
  const description = removeTags(collection?.description || '')
  const formatedDate = moment(collection?.publishedAt).format('ddd, MMMM DD')
  const blogTaxonomyLogo = useDefaultLogo()
  const slug = collection?.slug
  const [filterHeight, setFilterHeight] = useState(false)

  const searchKeyword = useDecodedKeyword(router.query.keyword)

  const [latestContentData, setLatestContentData] = useState<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any
    image: string
    slug: string
  }>({
    data: null,
    image: '',
    slug: '',
  })

  useEffect(() => {
    // Check if there is any data in the array
    if (collection?.contents?.length > 0) {
      const latestContent = collection?.contents?.at(-1)

      let latestContentImage = ''
      let latestContentSlug = ''
      if (latestContentData.slug !== '' && latestContentData.data !== null) {
        latestContentImage = useDefaultLogo(
          latestContentData.data?.defaultImage && latestContentData.data?.defaultImage.length > 0
            ? latestContentData.data?.defaultImage[0].url
            : null,
        )
        latestContentSlug = latestContentData.data?.slug
      } else {
        latestContentSlug = latestContent?.slug
        latestContentImage = useDefaultLogo(
          latestContent?.defaultImage && latestContent?.defaultImage?.length > 0
            ? latestContent?.defaultImage[0].url
            : null,
        )
      }

      setLatestContentData({
        data: latestContent,
        image: latestContentImage,
        slug: latestContentSlug,
      })
    } else {
      // If no data is available, reset the state
      setLatestContentData({
        data: null,
        image: '',
        slug: '',
      })
    }
  }, [])

  const [tabIndex, setTabIndex] = useState(0)
  const [vendorTotalCount, setVendorTotalCount] = useState(0)
  const getTabIndexFromRouter = (tab?: string | string[]) => {
    if (tab === 'directory') return 1
    if (tab === 'content') return 2
    return 0
  }

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const newTabIndex = getTabIndexFromRouter(router.query.tab)
    if (isPremium && tabIndex !== newTabIndex) {
      setTabIndex(newTabIndex)
    }
    if (!isPremium && newTabIndex === 0) {
      if (status === 'loading') {
        setTabIndex(-1)
      } else {
        setTabIndex(1)
      }
    } else {
      setTabIndex(newTabIndex)
    }
  }, [router, isPremium, status])

  useEffect(() => {
    // Simulating a loading delay, replace with your actual logic
    const timer = setTimeout(() => {
      setLoading(false)
    }, 1000)

    return () => clearTimeout(timer)
  }, [isPremium])

  const onSelectTab = (tabId: number) => {
    setTabIndex(tabId)
    const newTab = tabId === 1 ? 'directory' : 'content'
    let defaultSort = tabId === 1 ? 'Default' : 'Chronologically'
    const keyword = router.query.keyword
    defaultSort = searchKeyword ? 'bykeyword' : defaultSort

    updateQueryParams({
      tab: newTab,
      sort: defaultSort,
      page: undefined,
      keyword: (keyword as string | undefined) || undefined,
    })
  }

  const meta = (
    <Meta
      title={title}
      description={description}
      url={`/collections/${slug}/`}
      image={blogTaxonomyLogo}
      type="article"
      article={{
        authors: ['Nicola (Nikki) Shaver, Jeroen Plink'],
        section: 'Blogs',
        publishedTime: formatedDate,
        modifiedTime: formatedDate,
      }}
    />
  )

  if (loading) {
    return <p>Loading...</p>
  }

  /* if (!loading && !isPremium && status === 'authenticated') {
    return <p>Access Denied. You need a premium account to view this content.</p>
  } */
  return (
    <Main meta={meta}>
      <PremiumModal origin={`/blogs/${slug}/`} isOpen={showModal} onClose={() => setShowModal(false)} />
      <MainContainer id="mainContainer">
        <div className="bg-navy w-full">
          <h1 className="font-secondary font-bold text-[40px] leading-[48px] text-white text-center mt-[58px]">
            {title}
          </h1>
          <div className="flex flex-row !max-w-[1100px] w-full mx-auto pb-[28px] justify-center">
            <h2 className="font-secondary font-bold text-[18px] leading-[32px] text-turquoise text-center mt-[10px] px-5 md:px-0">
              {description}
            </h2>
          </div>
        </div>
        {latestContentData.data && (
          <div className="bg-clouds lg:min-h-[357px] w-full py-[30px] px-5">
            <Hidden mdDown>
              <div className="font-secondary font-medium text-[14px] leading-[21px] text-darkgray !max-w-[1100px] mx-auto">
                FEATURED ARTICLE
              </div>
            </Hidden>
            <div className="flex flex-row !max-w-[1100px] w-full mx-auto pt-[28px]">
              <Hidden mdDown>
                <div className="min-w-[240px] mr-[40px]">
                  <Image
                    className={cn('mr-[1.6875rem]', styles.blogLogo)}
                    src={latestContentData.data?.defaultImage?.[0]?.url || latestContentData.image}
                    width={1000}
                    height={1000}
                    priority
                    alt=""
                  />
                </div>
              </Hidden>
              <div>
                <Link href={`/contents/${latestContentData?.slug}`}>
                  <span className="font-secondary font-bold text-[28px] leading-[34px] text-textdark underline">
                    {latestContentData.data?.title}
                  </span>
                </Link>
                <div className="mt-[10px]">
                  {latestContentData.data?.writer && <ContentWriter writer={latestContentData.data?.writer} />}
                </div>
                <HtmlContentViewer className="mt-[8px]" html={latestContentData.data?.snippet || ''} />
              </div>
            </div>
          </div>
        )}
        <ThemeOnly theme="legaltech">
          <div className="h-[50px] flex flex-row justify-center mt-8">
            <ThemeOnly theme="legaltech">
              {collection?.contents.length > 0 ? (
                <TopicCategoriesTab id={2} activeTab={tabIndex} onSelectTab={onSelectTab}>
                  CONTENT
                </TopicCategoriesTab>
              ) : null}
            </ThemeOnly>
            {collection?.solutions.length > 0 ? (
              <TopicCategoriesTab id={1} activeTab={tabIndex} onSelectTab={onSelectTab} width="200">
                DIRECTORY {vendorTotalCount > 0 && `(${vendorTotalCount})`}
              </TopicCategoriesTab>
            ) : null}
          </div>
        </ThemeOnly>
        <div className="flex flex-row justify-center w-full">
          {tabIndex === 1 && (
            <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32 relative">
              <Hidden mdDown>
                <div className="w-1/4 pr-10 flex flex-col">
                  <SearchField className="my-8" />
                  <Sidebar filterOptions={filters} defaultSelectFilterIdx={1} filterHeight={filterHeight} />
                </div>
              </Hidden>

              <CollectionVendorPagination
                filterProps={filters}
                changeTotalCount={setVendorTotalCount}
                includeCompany={false}
                preAppliedFilters={{ collectionIds: collection.documentId! }}
                setFilterHeight={setFilterHeight}
              />
            </div>
          )}
          {tabIndex === 2 && (
            <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
              <Hidden mdDown>
                <div className="w-1/4 pr-10 flex flex-col">
                  <SearchField className="my-8" />
                  <Sidebar
                    filterOptions={
                      isVendor && status === 'authenticated'
                        ? contentFilters.filter((filter) => filter.title !== TaxonomyType.vendorTopic)
                        : contentFilters
                    }
                    // filtersDisabled={filtersDisabled}
                    defaultSelectFilterIdx={1}
                    onClickWhenDisabled={() => setShowModal(true)}
                  />
                </div>
              </Hidden>

              <CollectionContentPagination
                filterProps={
                  isVendor && status === 'authenticated'
                    ? contentFilters.filter((filter) => filter.title !== TaxonomyType.vendorTopic)
                    : contentFilters
                }
                showDocumentType
                // filtersDisabled={filtersDisabled}
                onClickWhenDisabled={() => setShowModal(true)}
                preAppliedFilters={{ collectionIds: collection.documentId! }}
                searchKeyword={searchKeyword}
              />
            </div>
          )}
        </div>
        <ThemeOnly theme="legaltech">
          <div className="mt-auto">
            <Footer />
          </div>
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default Collections

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  if (!params?.slug || THEME === 'ilta') {
    return {
      revalidate: 60,
      notFound: true,
    }
  }
  try {
    // if (!SHOW_COLLECTION || SHOW_COLLECTION == null) {
    //   return {
    //     notFound: true,
    //   }
    // }

    const slug = params.slug as string
    const { collections } = await withRetries(() => apolloSdk.getCollectionsBySlug({ slug }))
    const collection = collections && collections.length > 0 ? collections[0] : null
    if (!collection) {
      return {
        revalidate: 60,
        notFound: true,
      }
    }
    const filters = await getDirectoryFilters()
    const contentFiltersOptions = {
      subjectsFilter: { includeSubjectMatters: true, includeThemes: false },
      authorsFilter: { includeAuthors: true, includeExperts: true },
    }
    const contentFilters: FilterOption[] = await getContentFilters(contentFiltersOptions)

    return {
      props: {
        slug,
        collection,
        contentFilters: contentFilters,
        filters: filters,
      },
      revalidate: 60, // Only cache successful pages
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching collection:', error)
    return {
      notFound: true,
      revalidate: 5,
    }
  }
}

export async function getStaticPaths() {
  const { collections } = await apolloSdk.allCollectionSlug()
  const paths: Array<{ params: { slug: string } }> = []

  if (collections) {
    collections.forEach((item) => {
      if (!item) return

      paths.push({ params: { slug: item?.slug || '' } })
    })
  }
  return {
    paths,
    fallback: 'blocking',
  }
}
